import { TrackerImpl } from "./tracker";
import type { Tracker } from "./types";
import { log } from "@/services/logging/logger";
import { decodeSessionConfig } from "@/lib/window-session-config";

declare global {
  interface Window {
    tracker?: Tracker;
  }
}

/**
 * Initialize the tracker in the background without blocking app startup
 * @returns Promise that resolves when tracker is initialized
 */
export function initializeTrackerInBackground(): Promise<Tracker> {
  return new Promise((resolve, reject) => {
    // Use requestIdleCallback for better performance, fallback to setTimeout
    const scheduleInit = (callback: () => void) => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(callback, { timeout: 1000 });
      } else {
        setTimeout(callback, 1);
      }
    };

    scheduleInit(() => {
      try {
        if (!window.sessionConfig) {
          reject(new Error("Session config not found"));
          return;
        }

        // Accepts both server injection formats (base64 string / raw object)
        const decodedSessionConfig = decodeSessionConfig();
        if (!decodedSessionConfig) {
          reject(new Error("Session config is not a valid object"));
          return;
        }

        // Get the global tracker instance and initialize it
        const tracker = window.tracker;
        if (!tracker || !(tracker instanceof TrackerImpl)) {
          reject(new Error("Tracker instance not found"));
          return;
        }

        // Validate token exists
        if (
          !decodedSessionConfig.token ||
          typeof decodedSessionConfig.token !== "string"
        ) {
          reject(new Error("Token not found in session config"));
          return;
        }

        // Initialize with token from sessionConfig
        tracker.initialize(decodedSessionConfig.token);

        log(
          "info",
          "tracker",
          "Tracker initialized successfully in background",
        );
        resolve(tracker);
      } catch (error) {
        log(
          "error",
          "tracker",
          "Failed to initialize tracker",
          error instanceof Error ? error : new Error(String(error)),
        );
        reject(error);
      }
    });
  });
}
